// src/utils/municipality.js
// Kuntalogiikka: sv→fi aliaset, normalisointi, validointi + Google address_components -poiminta.

import municipalitiesFI from "../data/municipalities.fi.json";

/** Apufunktio: normalisoi merkkijono indeksiavaimeksi (ääkköset pois, lower-case) */
const normalizeKey = (s = "") =>
  String(s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

/** Kaikki kunnat (uniikit) aakkosissa suomalaisella lajittelulla */
export const MUNICIPALITIES = Array.from(
  new Set(municipalitiesFI.flatMap((r) => r.kunnat.map(String)))
).sort((a, b) => a.localeCompare(b, "fi"));

/** Nopeaan tarkistukseen: normalisoitu (ääkköset pois) → kanoninen kirjoitusasu */
const MUNICIPALITY_INDEX = new Map(
  MUNICIPALITIES.map((n) => [normalizeKey(n), n])
);

/** Ruotsi→suomi + paikallisaliaset (ei-kunnat → emokunta) */
const SV_FI_ALIASES = new Map([
  ["helsingfors", "Helsinki"],
  ["esbo", "Espoo"],
  ["vanda", "Vantaa"],
  ["abo", "Turku"],
  ["tammerfors", "Tampere"],
  ["kervo", "Kerava"],

  // Tuusula-erityiset:
  ["tusby", "Tuusula"], // ruotsiksi Tuusula

  // Taajamat / kylät → emokunta
  ["hyrylä", "Tuusula"],
  ["hyryla", "Tuusula"],
  ["kellokoski", "Tuusula"],
  ["jokela", "Tuusula"],
]);

/** Postinumero → kunta (laajenna tarvittaessa) */
const POSTCODE_TO_MUNI = new Map([
  ["04300", "Tuusula"], // Hyrylä / Tuusulan keskusta
  ["04410", "Tuusula"], // aiempi arvaus Tuusula-alueelle – tarkista haluatko tämän
  ["04430", "Tuusula"],
  ["04500", "Tuusula"],
  ["05400", "Tuusula"], // Jokela (tärkeä tälle bugille)
]);

/** Normalisoi kuntanimi ja palauta kanoninen nimi, jos tunnettu */
export function resolveMunicipality(input = "") {
  const raw = String(input || "").trim();
  if (!raw) return "";

  // Normalisoitu versio: diakriitit pois, lower-case
  const normalized = raw.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const lower = normalized.toLowerCase();

  // Pieni apufunktio: yritä yhdellä tokenilla
  const tryOne = (tokenLower) => {
    if (!tokenLower) return "";

    // 1) alias (sv→fi, taajamat → emokunta)
    const alias = SV_FI_ALIASES.get(tokenLower);
    if (alias) return alias;

    // 2) suora osuma kuntalistaan (ääkkösnormalisoitu)
    const direct = MUNICIPALITY_INDEX.get(tokenLower);
    if (direct) return direct;

    // 3) muodot kuten "tuusula kunta" / "tusby kommun"
    const strippedLower = tokenLower.replace(/\s+(kunta|kommun)\b/g, "");
    const strippedHit = MUNICIPALITY_INDEX.get(strippedLower);
    if (strippedHit) return strippedHit;

    return "";
  };

  // Ensin kokeillaan koko merkkijono sellaisenaan
  const wholeHit = tryOne(lower);
  if (wholeHit) return wholeHit;

  // Seuraavaksi pilkotaan merkkijono osiin, jotta muodot
  // kuten "05400 Jokela", "Jokela, Tuusula" tai "Jokela (Tuusula)"
  // osuvat edes johonkin tokeniin.
  const parts = normalized
    .replace(/[()]/g, " ")
    .split(/[,\s]+/)
    .map((p) => p.trim().toLowerCase())
    .filter((p) => p && !/^\d+$/.test(p)); // skippaa pelkät numerot (postinumerot yms.)

  // Käydään oikealta vasemmalle – loppupäässä on tyypillisesti kunta
  for (let i = parts.length - 1; i >= 0; i -= 1) {
    const hit = tryOne(parts[i]);
    if (hit) return hit;
  }

  return "";
}

/** Poimi kunta Google Placesin address_components -taulukosta */
export function pickMunicipalityFromComponents(components = []) {
  if (!Array.isArray(components)) return "";

  const byType = (type) =>
    components.find((c) => Array.isArray(c.types) && c.types.includes(type))?.long_name || "";

  // 1) Postinumero-ohitus (pois välilyönnit)
  const postal = (byType("postal_code") || "").replace(/\s/g, "");
  if (postal && POSTCODE_TO_MUNI.has(postal)) {
    return POSTCODE_TO_MUNI.get(postal);
  }

  // 2) Järjestys: postal_town → admin_level_3 → locality → sublocality → neighborhood → admin_level_2
  const order = [
    "postal_town",
    "administrative_area_level_3",
    "locality",
    "sublocality",
    "neighborhood",
    "administrative_area_level_2",
  ];

  for (const t of order) {
    const v = byType(t);
    const canon = resolveMunicipality(v);
    if (canon) return canon;
  }

  // 3) Viimeinen yritys: käy kaikki long_name:t läpi
  for (const c of components) {
    const canon = resolveMunicipality(c?.long_name || "");
    if (canon) return canon;
  }

  return "";
}

/** Onko annettu arvo kelvollinen kunta (normalisoinnin jälkeen) */
export function isValidMunicipality(value = "") {
  return Boolean(resolveMunicipality(value));
}
